/**
 * Graph Explorer - Clustering Module
 * Client-side vis.js clustering for large graph performance optimization.
 *
 * Uses vis.js built-in clustering API to group nodes by attribute (default: resource_name),
 * dramatically reducing the number of rendered elements and physics computations.
 *
 * Architecture:
 * - Auto-clusters when node count exceeds configurable threshold (default: 500)
 * - Clusters are expandable via double-click
 * - Cluster-aware: works alongside server-side aggregation (AGGREGATE nodes are excluded)
 * - Preserves colors from the active color palette
 */

(function(global) {
    'use strict';

    // ============================================
    // Clustering State
    // ============================================
    const clusterState = {
        enabled: false,           // Whether clustering is currently active
        autoCluster: true,        // Auto-cluster above threshold
        clusterThreshold: 500,    // Node count threshold for auto-clustering
        clusterAttribute: 'resource_name',  // Attribute to cluster by
        minClusterSize: 3,        // Minimum members to form a cluster
        maxClusters: 500,         // Target number of sub-clusters to produce
        activeClusters: {},       // { clusterId: { attribute, memberCount, memberIds } }
        expandedClusters: new Set(), // Cluster IDs that user has expanded (don't re-cluster)
        syntheticClusterEdgeIds: new Set(), // Synthetic display edges materialized from stored edges
        clusterOptions: {},       // Cached cluster options per attribute value
    };

    // ============================================
    // Initialization
    // ============================================
    function init(options = {}) {
        if (options.autoCluster !== undefined) clusterState.autoCluster = options.autoCluster;
        if (options.clusterThreshold) clusterState.clusterThreshold = options.clusterThreshold;
        if (options.clusterAttribute) clusterState.clusterAttribute = options.clusterAttribute;
        if (options.minClusterSize) clusterState.minClusterSize = options.minClusterSize;
        if (options.maxClusters) {
            clusterState.maxClusters = options.maxClusters;
        } else {
            clusterState.maxClusters = _getDefaultMaxClusters();
        }
        console.log('[ExplorerClustering] Initialized', {
            autoCluster: clusterState.autoCluster,
            threshold: clusterState.clusterThreshold,
            attribute: clusterState.clusterAttribute,
            maxClusters: clusterState.maxClusters,
        });
    }

    // ============================================
    // Core Clustering
    // ============================================

    /**
     * Cluster nodes by a shared attribute value (e.g. resource_name) using
     * a **budget-based** approach that maximises visible detail:
     *
     * 1. Group nodes by attribute value, sort groups smallest → largest.
     * 2. Walk from smallest: keep entire groups as individual nodes until
     *    adding the next group would exceed the budget (maxClusters).
     * 3. The "boundary" group: keep the highest-degree nodes individual,
     *    cluster the remainder into one vis.js cluster.
     * 4. All larger groups: cluster ALL their nodes into one vis.js
     *    cluster each.
     *
     * Result: small / rare types are fully visible; large types are
     * collapsed with a label like "Role (2487)".
     *
     * @param {string} [attribute] - Node property to group by
     * @param {number} [minSize]   - Unused (compat), budget drives clustering
     */
    function clusterByAttribute(attribute, minSize) {
        var network = _getNetwork();
        var nodes = _getNodes();
        if (!network || !nodes) return;

        attribute = attribute || clusterState.clusterAttribute;
        var budget = clusterState.maxClusters;
        var minMembersToCluster = Math.max(2, minSize || clusterState.minClusterSize || 3);

        // First collapse any existing clusters
        collapseAll();

        // Group nodes by attribute value
        var groups = {};
        var allNodes = nodes.get();
        var nodeLookup = {};
        allNodes.forEach(function(node) {
            if (node.is_cluster) return;
            nodeLookup[node.id] = node;
            var value = node[attribute] || node.group || 'Other';
            if (!groups[value]) groups[value] = [];
            groups[value].push(node);
        });

        var edgePool = _getEdgePool();
        var adjacency = _buildAdjacencyMap(edgePool);

        // Sort groups smallest → largest
        var sortedGroups = Object.keys(groups).map(function(key) {
            return { value: key, members: groups[key] };
        }).sort(function(a, b) { return a.members.length - b.members.length; });

        // --- Compute node degree from stored edges (for boundary selection) ---
        var degree = _computeNodeDegree(edgePool);

        // --- Walk groups and decide: keep / boundary / cluster ---
        var used = 0;                       // individual nodes consumed
        var keepGroups = [];                 // fully individual
        var boundaryGroup = null;           // partially individual
        var boundaryKeep = 0;               // how many individuals from boundary
        var clusterGroups = [];             // fully clustered

        for (var gi = 0; gi < sortedGroups.length; gi++) {
            var g = sortedGroups[gi];
            if (used + g.members.length <= budget) {
                // Entire group fits → keep all individual
                keepGroups.push(g);
                used += g.members.length;
            } else if (!boundaryGroup && used < budget) {
                // This group crosses the boundary
                boundaryGroup = g;
                boundaryKeep = budget - used;
                used = budget;
                // Everything after this is fully clustered
            } else {
                clusterGroups.push(g);
            }
        }

        // --- Create vis.js clusters ---
        var clustersCreated = 0;
        var palette = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.getColorPalette)
            ? ExplorerConstants.getColorPalette() : [];

        // Build a global color index: smallest types first, then boundary, then large
        var allGroupValues = sortedGroups.map(function(g) { return g.value; });

        // Helper: create one vis.js cluster from a set of node IDs
        function _createCluster(value, memberNodes, colorIndex, subgroup, subgroupIndex, subgroupTotal) {
            var memberIds = new Set(memberNodes.map(function(n) { return n.id; }));
            var subgroupHash = subgroup && subgroup.signature
                ? _hashString(attribute + ':' + value + ':' + subgroup.signature + ':' + subgroupIndex)
                : null;
            var clusterId = subgroupHash
                ? 'cluster:' + attribute + ':' + value + ':' + subgroupHash
                : 'cluster:' + attribute + ':' + value;

            if (clusterState.expandedClusters.has(clusterId)) return false;

            var color = palette[colorIndex % palette.length] || '#808080';
            var memberCount = memberNodes.length;
            var clusterSize = Math.round(15 + 35 * (Math.log(memberCount + 1) / Math.log(1000)));
            clusterSize = Math.min(clusterSize, 60);
            var title = 'Cluster: ' + value + '\n' + memberCount + ' nodes\nDouble-click to expand';
            if (subgroup && subgroup.anchorLabels && subgroup.anchorLabels.length > 0) {
                title += '\nConnected to: ' + subgroup.anchorLabels.join(', ');
            }
            var visibleLabel = value + ' (' + memberCount + ')';
            if (subgroupTotal > 1 && subgroup && subgroup.anchorLabels && subgroup.anchorLabels.length === 1) {
                visibleLabel = value + ' ↔ ' + subgroup.anchorLabels[0] + ' (' + memberCount + ')';
            }

            try {
                network.cluster({
                    joinCondition: function(nodeOptions) {
                        return memberIds.has(nodeOptions.id);
                    },
                    clusterNodeProperties: {
                        id: clusterId,
                        label: visibleLabel,
                        title: title,
                        shape: 'dot',
                        size: clusterSize,
                        color: {
                            background: color,
                            border: _darkenColor(color, 0.3),
                            highlight: {
                                background: _lightenColor(color, 0.2),
                                border: color
                            }
                        },
                        borderWidth: 3,
                        font: {
                            size: Math.min(16, 12 + Math.floor(memberCount / 50)),
                            color: _getClusterFontColor(),
                            bold: true
                        },
                        mass: Math.max(1, memberCount * 0.1),
                        shadow: false,
                        _isCluster: true,
                        _clusterAttribute: attribute,
                        _clusterValue: value,
                        _memberCount: memberCount,
                    },
                    clusterEdgeProperties: {
                        color: { color: color, opacity: 0.6 },
                        width: 2,
                        smooth: { enabled: true, type: 'continuous' },
                    },
                    processProperties: function(clusterOptions, childNodes) {
                        clusterOptions.label = visibleLabel;
                        return clusterOptions;
                    }
                });

                clusterState.activeClusters[clusterId] = {
                    attribute: attribute,
                    value: value,
                    memberCount: memberCount,
                    memberIds: Array.from(memberIds),
                    color: color,
                    anchorIds: subgroup && subgroup.anchorIds ? subgroup.anchorIds.slice() : [],
                    anchorLabels: subgroup && subgroup.anchorLabels ? subgroup.anchorLabels.slice() : [],
                };
                return true;
            } catch (err) {
                console.warn('[ExplorerClustering] Failed to cluster "' + value + '":', err);
                return false;
            }
        }

        function _clusterConnectivityAwareGroup(value, members, colorIndex) {
            var subgroups = _buildConnectivityAwareSubgroups(value, members, attribute, adjacency, nodeLookup, minMembersToCluster);
            for (var subgroupIndex = 0; subgroupIndex < subgroups.length; subgroupIndex++) {
                var subgroup = subgroups[subgroupIndex];
                if (subgroup.members.length < minMembersToCluster) {
                    continue;
                }
                if (_createCluster(value, subgroup.members, colorIndex, subgroup, subgroupIndex, subgroups.length)) {
                    clustersCreated++;
                }
            }
        }

        // Boundary group: keep highest-degree nodes individual, cluster rest
        if (boundaryGroup) {
            var bMembers = boundaryGroup.members.slice();
            // Sort by degree descending — keep the most connected individual
            bMembers.sort(function(a, b) {
                return (degree[b.id] || 0) - (degree[a.id] || 0);
            });
            var toCluster = bMembers.slice(boundaryKeep); // excess → cluster
            if (toCluster.length >= minMembersToCluster) {
                var ci = allGroupValues.indexOf(boundaryGroup.value);
                _clusterConnectivityAwareGroup(boundaryGroup.value, toCluster, ci);
            }
            console.log('[ExplorerClustering] Boundary group "' + boundaryGroup.value +
                '": kept ' + boundaryKeep + ' individual, clustered ' + toCluster.length);
        }

        // Fully-clustered groups: one vis.js cluster per group
        for (var ci2 = 0; ci2 < clusterGroups.length; ci2++) {
            var cg = clusterGroups[ci2];
            var colorIdx = allGroupValues.indexOf(cg.value);
            _clusterConnectivityAwareGroup(cg.value, cg.members, colorIdx);
        }

        clusterState.enabled = clustersCreated > 0;
        if (clustersCreated > 0) {
            _syncSyntheticClusterEdges();
        }
        var keptCount = keepGroups.reduce(function(s, g) { return s + g.members.length; }, 0);
        var bKept = boundaryGroup ? boundaryKeep : 0;
        console.log('[ExplorerClustering] Budget ' + budget + ': ' +
            keepGroups.length + ' types fully individual (' + keptCount + ' nodes), ' +
            (boundaryGroup ? '1 boundary type (' + bKept + ' kept)' : 'no boundary') + ', ' +
            clusterGroups.length + ' types fully clustered, ' +
            clustersCreated + ' clusters created from ' + allNodes.length + ' nodes');
    }

    /**
     * Cluster by node connectivity — collapses high-degree hub neighborhoods.
     * @param {number} [hubsize] - Minimum connections to be considered a hub
     */
    function clusterByHubsize(hubsize) {
        var network = _getNetwork();
        if (!network) return;

        hubsize = hubsize || 10;

        try {
            network.clusterByHubsize(hubsize, {
                clusterNodeProperties: {
                    shape: 'dot',
                    borderWidth: 3,
                    font: { bold: true, color: _getClusterFontColor() },
                    shadow: false,
                    _isCluster: true,
                },
            });
            console.log('[ExplorerClustering] Clustered by hubsize >', hubsize);
            clusterState.enabled = true;
        } catch (err) {
            console.warn('[ExplorerClustering] Hub clustering failed:', err);
        }
    }

    /**
     * Expand (open) a specific cluster, revealing its member nodes.
     * @param {string} clusterId - The cluster node ID to expand
     * @param {boolean} [animate=true] - Whether to animate the expansion
     */
    function expandCluster(clusterId) {
        var network = _getNetwork();
        if (!network) return false;

        try {
            if (!network.isCluster(clusterId)) return false;

            // Remember which nodes were inside this cluster BEFORE opening it
            var memberNodeIds;
            try { memberNodeIds = network.getNodesInCluster(clusterId); }
            catch(e) { memberNodeIds = []; }

            // Remember this cluster was expanded so we don't re-cluster it
            clusterState.expandedClusters.add(clusterId);
            delete clusterState.activeClusters[clusterId];

            network.openCluster(clusterId, {
                releaseFunction: function(clusterPosition, containedNodesPositions) {
                    // Position children in a circle around the cluster position
                    var nodeIds = Object.keys(containedNodesPositions);
                    var radius = Math.max(80, nodeIds.length * 15);
                    var result = {};
                    nodeIds.forEach(function(nodeId, i) {
                        var angle = (2 * Math.PI * i) / nodeIds.length;
                        result[nodeId] = {
                            x: clusterPosition.x + radius * Math.cos(angle),
                            y: clusterPosition.y + radius * Math.sin(angle),
                        };
                    });
                    return result;
                }
            });

            // Inject edges for the newly-exposed member nodes from the
            // stored edge pool (edges kept outside vis.js for performance).
            _injectEdgesForNodes(memberNodeIds);
            _syncSyntheticClusterEdges();

            // Brief physics settle for newly expanded nodes
            setTimeout(function() {
                if (typeof GraphExplorer !== 'undefined' && GraphExplorer.improveLayout) {
                    GraphExplorer.improveLayout(30);
                }
            }, 50);

            console.log('[ExplorerClustering] Expanded cluster:', clusterId);
            _checkClusteringState();
            return true;
        } catch (err) {
            console.warn('[ExplorerClustering] Failed to expand cluster:', clusterId, err);
            return false;
        }
    }

    /**
     * Expand all clusters, revealing all member nodes.
     */
    function expandAll() {
        var network = _getNetwork();
        if (!network) return;

        var clusterIds = Object.keys(clusterState.activeClusters);
        clusterIds.forEach(function(clusterId) {
            try {
                if (network.isCluster(clusterId)) {
                    clusterState.expandedClusters.add(clusterId);
                    network.openCluster(clusterId);
                }
            } catch(e) { /* cluster may already be open */ }
        });

        clusterState.activeClusters = {};
        _removeSyntheticClusterEdges();
        clusterState.enabled = false;
        // Inject all stored edges back into vis.js DataSet
        _injectAllEdges();
        console.log('[ExplorerClustering] Expanded all clusters');
    }

    /**
     * Collapse all expanded clusters back to clustered state.
     * Resets expanded tracking.
     */
    function collapseAll() {
        var network = _getNetwork();
        if (network) {
            _removeSyntheticClusterEdges();
            var opened = true;
            while (opened) {
                opened = false;
                var visibleIds = (network.body && network.body.nodeIndices)
                    ? network.body.nodeIndices.slice()
                    : [];
                for (var i = 0; i < visibleIds.length; i++) {
                    var nodeId = visibleIds[i];
                    try {
                        if (network.isCluster(nodeId)) {
                            network.openCluster(nodeId);
                            opened = true;
                        }
                    } catch (e) {
                        // Ignore stale cluster ids during rebuild.
                    }
                }
            }
        }

        _injectAllEdges();
        clusterState.activeClusters = {};
        clusterState.expandedClusters.clear();
        clusterState.enabled = false;
    }

    // ============================================
    // Auto-Clustering Logic
    // ============================================

    /**
     * Check if auto-clustering should fire and apply it.
     * Called after graph data is loaded.
     *
     * @param {number} nodeCount - Number of nodes in the loaded graph
     * @param {string} [attribute] - Attribute to cluster by
     * @returns {boolean} True if clustering was applied
     */
    function autoClusterIfNeeded(nodeCount, attribute) {
        if (!clusterState.autoCluster) return false;
        if (nodeCount < clusterState.clusterThreshold) return false;

        attribute = attribute || clusterState.clusterAttribute;
        clusterByAttribute(attribute);
        return clusterState.enabled;
    }

    // ============================================
    // Cluster Queries
    // ============================================

    /**
     * Check if a node ID is a cluster.
     * @param {string} nodeId
     * @returns {boolean}
     */
    function isCluster(nodeId) {
        var network = _getNetwork();
        if (!network) return false;
        try {
            return network.isCluster(nodeId);
        } catch(e) {
            return false;
        }
    }

    /**
     * Get member node IDs inside a cluster.
     * @param {string} clusterId
     * @returns {Array}
     */
    function getClusterMembers(clusterId) {
        var network = _getNetwork();
        if (!network) return [];
        try {
            return network.getNodesInCluster(clusterId);
        } catch(e) {
            return [];
        }
    }

    /**
     * Get info about all active clusters.
     * @returns {Array<{clusterId, attribute, value, memberCount, color}>}
     */
    function getClusterInfo() {
        return Object.keys(clusterState.activeClusters).map(function(id) {
            var info = clusterState.activeClusters[id];
            return {
                clusterId: id,
                attribute: info.attribute,
                value: info.value,
                memberCount: info.memberCount,
                color: info.color,
            };
        });
    }

    /**
     * Get summary stats for the legend.
     * @returns {Object} { totalClusters, totalClusteredNodes, clustersByAttribute: { value: count } }
     */
    function getClusterStats() {
        var totalClusters = Object.keys(clusterState.activeClusters).length;
        var totalClusteredNodes = 0;
        var byAttribute = {};
        Object.values(clusterState.activeClusters).forEach(function(info) {
            totalClusteredNodes += info.memberCount;
            byAttribute[info.value] = (byAttribute[info.value] || 0) + info.memberCount;
        });
        return {
            totalClusters: totalClusters,
            totalClusteredNodes: totalClusteredNodes,
            clustersByAttribute: byAttribute,
        };
    }

    // ============================================
    // Configuration
    // ============================================

    function setAutoCluster(enabled) {
        clusterState.autoCluster = enabled;
        console.log('[ExplorerClustering] Auto-cluster:', enabled);
    }

    function setClusterThreshold(threshold) {
        clusterState.clusterThreshold = threshold;
        console.log('[ExplorerClustering] Threshold set to:', threshold);
    }

    function setClusterAttribute(attribute) {
        clusterState.clusterAttribute = attribute;
        console.log('[ExplorerClustering] Attribute set to:', attribute);
    }

    function setMinClusterSize(size) {
        clusterState.minClusterSize = size;
        console.log('[ExplorerClustering] Min cluster size:', size);
    }

    function setMaxClusters(count) {
        clusterState.maxClusters = Math.max(10, count);
        console.log('[ExplorerClustering] Max clusters (budget):', clusterState.maxClusters);
    }

    function reclusterCurrentGraph() {
        var nodes = _getNodes();
        if (!nodes) return false;

        var nodeCount = nodes.length || nodes.get().length || 0;
        if (nodeCount < clusterState.clusterThreshold) {
            collapseAll();
            console.log('[ExplorerClustering] Recluster skipped - below threshold:', nodeCount);
            return false;
        }

        clusterByAttribute(clusterState.clusterAttribute, clusterState.minClusterSize);
        if (clusterState.enabled && typeof GraphExplorer !== 'undefined' && GraphExplorer.improveLayout) {
            setTimeout(function() {
                GraphExplorer.improveLayout(30);
            }, 0);
        }
        return clusterState.enabled;
    }

    // ============================================
    // Private Helpers
    // ============================================

    function _getNetwork() {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
            return GraphExplorer.state.network;
        }
        return null;
    }

    function _getNodes() {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
            return GraphExplorer.state.nodes;
        }
        return null;
    }

    function _getDefaultMaxClusters() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.hasStandaloneSnapshot && ExplorerAPI.hasStandaloneSnapshot()) {
            return 10000;
        }
        return 500;
    }

    function _getEdges() {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
            return GraphExplorer.state.edges;
        }
        return null;
    }

    function _getEdgePool() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getStoredEdges) {
            var storedEdges = ExplorerAPI.getStoredEdges();
            if (storedEdges && storedEdges.length > 0) {
                return storedEdges;
            }
        }
        var edges = _getEdges();
        return edges ? edges.get() : [];
    }

    function _computeNodeDegree(edgePool) {
        var degree = {};
        for (var edgeIndex = 0; edgeIndex < edgePool.length; edgeIndex++) {
            var edge = edgePool[edgeIndex];
            degree[edge.from] = (degree[edge.from] || 0) + 1;
            degree[edge.to] = (degree[edge.to] || 0) + 1;
        }
        return degree;
    }

    function _buildAdjacencyMap(edgePool) {
        var adjacency = {};
        for (var edgeIndex = 0; edgeIndex < edgePool.length; edgeIndex++) {
            var edge = edgePool[edgeIndex];
            if (!adjacency[edge.from]) adjacency[edge.from] = [];
            if (!adjacency[edge.to]) adjacency[edge.to] = [];
            adjacency[edge.from].push(edge.to);
            adjacency[edge.to].push(edge.from);
        }
        return adjacency;
    }

    function _buildConnectivityAwareSubgroups(groupValue, memberNodes, attribute, adjacency, nodeLookup, minMembersToCluster) {
        var subgroups = {};
        for (var memberIndex = 0; memberIndex < memberNodes.length; memberIndex++) {
            var node = memberNodes[memberIndex];
            var neighbors = adjacency[node.id] || [];
            var anchorIds = [];
            var anchorLabels = [];

            for (var neighborIndex = 0; neighborIndex < neighbors.length; neighborIndex++) {
                var neighborId = neighbors[neighborIndex];
                var neighbor = nodeLookup[neighborId];
                if (!neighbor) continue;
                var neighborValue = neighbor[attribute] || neighbor.group || 'Other';
                if (neighborValue === groupValue) continue;
                anchorIds.push(neighborId);
                anchorLabels.push(neighbor.label || neighborValue || neighborId);
            }

            anchorIds = Array.from(new Set(anchorIds)).sort();
            anchorLabels = Array.from(new Set(anchorLabels)).sort();
            var signature = anchorIds.length ? anchorIds.join('|') : '__isolated__';
            if (!subgroups[signature]) {
                subgroups[signature] = {
                    signature: signature,
                    members: [],
                    anchorIds: anchorIds,
                    anchorLabels: anchorLabels.slice(0, 3),
                };
            }
            subgroups[signature].members.push(node);
        }

        var subgroupList = Object.keys(subgroups).map(function(signature) {
            return subgroups[signature];
        }).sort(function(a, b) {
            if (a.members.length !== b.members.length) {
                return a.members.length - b.members.length;
            }
            return (a.signature || '').localeCompare(b.signature || '');
        });

        if (subgroupList.length <= 1) {
            return subgroupList;
        }

        var mergedSmallSubgroup = null;
        var normalizedSubgroups = [];
        for (var subgroupIndex = 0; subgroupIndex < subgroupList.length; subgroupIndex++) {
            var subgroup = subgroupList[subgroupIndex];
            if (subgroup.members.length < minMembersToCluster) {
                if (!mergedSmallSubgroup) {
                    mergedSmallSubgroup = {
                        signature: 'merged:' + groupValue,
                        members: [],
                        anchorIds: [],
                        anchorLabels: [],
                    };
                }
                mergedSmallSubgroup.members = mergedSmallSubgroup.members.concat(subgroup.members);
                mergedSmallSubgroup.anchorIds = mergedSmallSubgroup.anchorIds.concat(subgroup.anchorIds || []);
                mergedSmallSubgroup.anchorLabels = mergedSmallSubgroup.anchorLabels.concat(subgroup.anchorLabels || []);
            } else {
                normalizedSubgroups.push(subgroup);
            }
        }

        if (mergedSmallSubgroup && mergedSmallSubgroup.members.length > 0) {
            mergedSmallSubgroup.anchorIds = Array.from(new Set(mergedSmallSubgroup.anchorIds)).sort();
            mergedSmallSubgroup.anchorLabels = Array.from(new Set(mergedSmallSubgroup.anchorLabels)).sort().slice(0, 3);
            normalizedSubgroups.push(mergedSmallSubgroup);
        }

        return normalizedSubgroups.sort(function(a, b) {
            if (a.members.length !== b.members.length) {
                return a.members.length - b.members.length;
            }
            return (a.signature || '').localeCompare(b.signature || '');
        });
    }

    function _removeSyntheticClusterEdges() {
        if (!clusterState.syntheticClusterEdgeIds || clusterState.syntheticClusterEdgeIds.size === 0) {
            return;
        }
        var edges = _getEdges();
        if (edges) {
            try {
                edges.remove(Array.from(clusterState.syntheticClusterEdgeIds));
            } catch (error) {
                console.warn('[ExplorerClustering] Failed removing synthetic cluster edges:', error);
            }
        }
        clusterState.syntheticClusterEdgeIds.clear();
    }

    function _resolveVisibleRepresentative(nodeId, visibleNodeSet, memberToClusterMap) {
        if (memberToClusterMap[nodeId]) {
            return memberToClusterMap[nodeId];
        }
        if (visibleNodeSet[nodeId]) {
            return nodeId;
        }
        return null;
    }

    function _syncSyntheticClusterEdges() {
        var network = _getNetwork();
        var edges = _getEdges();
        if (!network || !edges) return;

        _removeSyntheticClusterEdges();

        var edgePool = _getEdgePool();
        if (!edgePool || edgePool.length === 0) {
            return;
        }

        var visibleNodeSet = {};
        var visibleNodeIds = (network.body && network.body.nodeIndices) ? network.body.nodeIndices.slice() : [];
        for (var visibleIndex = 0; visibleIndex < visibleNodeIds.length; visibleIndex++) {
            visibleNodeSet[visibleNodeIds[visibleIndex]] = true;
        }

        var memberToClusterMap = {};
        Object.keys(clusterState.activeClusters).forEach(function(clusterId) {
            var clusterInfo = clusterState.activeClusters[clusterId];
            clusterInfo.memberIds.forEach(function(memberId) {
                memberToClusterMap[memberId] = clusterId;
            });
        });

        var aggregates = {};
        for (var edgeIndex = 0; edgeIndex < edgePool.length; edgeIndex++) {
            var edge = edgePool[edgeIndex];
            var fromRepresentative = _resolveVisibleRepresentative(edge.from, visibleNodeSet, memberToClusterMap);
            var toRepresentative = _resolveVisibleRepresentative(edge.to, visibleNodeSet, memberToClusterMap);
            if (!fromRepresentative || !toRepresentative || fromRepresentative === toRepresentative) {
                continue;
            }
            if (fromRepresentative === edge.from && toRepresentative === edge.to) {
                continue;
            }

            var edgeLabel = edge.label || edge.name || edge.type || '';
            var aggregateKey = fromRepresentative + '::' + toRepresentative + '::' + edgeLabel;
            if (!aggregates[aggregateKey]) {
                aggregates[aggregateKey] = {
                    id: 'cluster-edge:' + _hashString(aggregateKey),
                    from: fromRepresentative,
                    to: toRepresentative,
                    label: edgeLabel,
                    count: 0,
                    arrows: edge.arrows,
                    dashes: edge.dashes,
                };
            }
            aggregates[aggregateKey].count += 1;
        }

        var syntheticEdges = Object.keys(aggregates).map(function(key) {
            var aggregate = aggregates[key];
            var edgeWidth = Math.min(8, Math.max(2, 1 + Math.log(aggregate.count + 1)));
            return {
                id: aggregate.id,
                from: aggregate.from,
                to: aggregate.to,
                label: aggregate.label,
                width: edgeWidth,
                arrows: aggregate.arrows,
                dashes: aggregate.dashes,
                physics: false,
                smooth: { enabled: true, type: 'continuous' },
                title: (aggregate.label || 'Connection') + '\n' + aggregate.count + ' underlying edge' + (aggregate.count === 1 ? '' : 's'),
                _syntheticClusterEdge: true,
                _syntheticEdgeCount: aggregate.count,
            };
        });

        if (syntheticEdges.length > 0) {
            edges.add(syntheticEdges);
            clusterState.syntheticClusterEdgeIds = new Set(syntheticEdges.map(function(edge) { return edge.id; }));
            _applyCurrentEdgeSettings(edges, syntheticEdges);
            console.log('[ExplorerClustering] Materialized ' + syntheticEdges.length + ' synthetic cluster edges');
        }
    }

    function _hashString(value) {
        var hash = 0;
        for (var index = 0; index < value.length; index++) {
            hash = ((hash << 5) - hash) + value.charCodeAt(index);
            hash |= 0;
        }
        return Math.abs(hash).toString(36);
    }

    function _checkClusteringState() {
        var remaining = Object.keys(clusterState.activeClusters).length;
        clusterState.enabled = remaining > 0;
    }

    /**
     * Inject edges from the stored edge pool (ExplorerAPI._storedEdges)
     * for a given set of node IDs.  Called when a cluster is expanded.
     * Only injects edges where BOTH endpoints are currently visible
     * (i.e. not inside another cluster).
     */
    function _injectEdgesForNodes(nodeIds) {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.getStoredEdges) return;
        var storedEdges = ExplorerAPI.getStoredEdges();
        if (!storedEdges || storedEdges.length === 0) {
            // No stored pool — edges are already in vis.js DataSet (hidden).
            // Fall back to revealing hidden edges.
            _revealEdgesForNodes(nodeIds);
            return;
        }

        var edges = _getEdges();
        var network = _getNetwork();
        if (!edges || !network) return;

        // Build set of node IDs that are currently VISIBLE on canvas
        var visibleSet = {};
        var visibleIds = network.body.nodeIndices || [];
        for (var v = 0; v < visibleIds.length; v++) visibleSet[visibleIds[v]] = true;
        // Also add the newly expanded member nodes (they might not be
        // in nodeIndices yet since openCluster just ran).
        if (nodeIds) {
            for (var n = 0; n < nodeIds.length; n++) visibleSet[nodeIds[n]] = true;
        }

        // Find edges where both endpoints are visible and not yet in DataSet
        var existingEdgeIds = {};
        var currentEdges = edges.get();
        for (var c = 0; c < currentEdges.length; c++) existingEdgeIds[currentEdges[c].id] = true;

        var toAdd = [];
        for (var i = 0; i < storedEdges.length; i++) {
            var e = storedEdges[i];
            if (!existingEdgeIds[e.id] && visibleSet[e.from] && visibleSet[e.to]) {
                toAdd.push(e);
            }
        }

        if (toAdd.length > 0) {
            edges.add(toAdd);
            _applyCurrentEdgeSettings(edges, toAdd);
            console.log('[ExplorerClustering] Injected ' + toAdd.length + ' edges for expanded cluster');
        }

        _syncSyntheticClusterEdges();

        // Apply node-level settings (labels, colors, sizing) to the
        // newly-revealed member nodes so they match the current config.
        _applyCurrentNodeSettings(nodeIds);
    }

    /**
     * Inject ALL stored edges back into vis.js DataSet.
     * Called when all clusters are expanded.
     */
    function _injectAllEdges() {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.getStoredEdges) return;
        var storedEdges = ExplorerAPI.getStoredEdges();
        if (!storedEdges || storedEdges.length === 0) {
            // Fall back to revealing hidden edges already in DataSet.
            _revealAllEdges();
            return;
        }

        var edges = _getEdges();
        if (!edges) return;

        // Avoid duplicates
        var existingEdgeIds = {};
        var currentEdges = edges.get();
        for (var c = 0; c < currentEdges.length; c++) existingEdgeIds[currentEdges[c].id] = true;

        var toAdd = [];
        for (var i = 0; i < storedEdges.length; i++) {
            if (!existingEdgeIds[storedEdges[i].id]) {
                toAdd.push(storedEdges[i]);
            }
        }

        if (toAdd.length > 0) {
            edges.add(toAdd);
            _applyCurrentEdgeSettings(edges, toAdd);
            if (typeof GraphExplorer !== 'undefined' && typeof GraphExplorer.storeOriginalEdgeColors === 'function') {
                GraphExplorer.storeOriginalEdgeColors();
            }
            console.log('[ExplorerClustering] Injected all ' + toAdd.length + ' stored edges');
        }

        _removeSyntheticClusterEdges();

        // Clear the stored pool — edges are now in vis.js
        if (ExplorerAPI.clearStoredEdges) ExplorerAPI.clearStoredEdges();
    }

    /**
     * Apply current visual settings to newly-injected edges:
     *  - edge color by label
     *  - hide labels if edgeLabelsVisible is false
     */
    function _applyCurrentEdgeSettings(edgesDataSet, edgeArray) {
        if (typeof GraphExplorer === 'undefined') return;

        // 1. Color edges by label
        if (typeof GraphExplorer.setEdgeColorByLabel === 'function') {
            GraphExplorer.setEdgeColorByLabel();
        }

        // 2. Hide edge labels if setting is off
        var labelsVisible = GraphExplorer.state && GraphExplorer.state.edgeLabelsVisible;
        if (!labelsVisible && edgesDataSet) {
            var updates = [];
            for (var i = 0; i < edgeArray.length; i++) {
                var e = edgeArray[i];
                if (e.label !== undefined && e.label !== '') {
                    updates.push({
                        id: e.id,
                        hiddenLabel: e.label,
                        hiddenFontSize: (e.font && e.font.size) || 12,
                        font: Object.assign({}, e.font, { size: 0 })
                    });
                }
            }
            if (updates.length > 0) {
                edgesDataSet.update(updates);
            }
        }
    }

    /**
     * Apply current visual settings to newly-revealed nodes from an
     * expanded cluster (label visibility, color-by-attribute, size).
     */
    function _applyCurrentNodeSettings(nodeIds) {
        if (typeof GraphExplorer === 'undefined' || !nodeIds || nodeIds.length === 0) return;
        var nodes = _getNodes();
        if (!nodes) return;

        // 1. Node label visibility
        var nodeLabelsVisible = GraphExplorer.state && GraphExplorer.state.nodeLabelsVisible;
        if (nodeLabelsVisible === false) {
            var updates = [];
            for (var i = 0; i < nodeIds.length; i++) {
                var node = nodes.get(nodeIds[i]);
                if (node && node.label !== undefined && node.label !== '' && node.hiddenLabel === undefined) {
                    updates.push({
                        id: node.id,
                        hiddenLabel: node.label,
                        label: ''
                    });
                }
            }
            if (updates.length > 0) nodes.update(updates);
        }

        // 2. Re-apply color-by-attribute so the new nodes pick up
        //    the palette from the current legendColorMap.
        if (typeof GraphExplorer.setColorByAttribute === 'function') {
            var colorAttr = (GraphExplorer.state && GraphExplorer.state.currentColorAttribute) || 'resource_name';
            GraphExplorer.setColorByAttribute(colorAttr);
        }

        // 3. Re-apply size scale
        if (typeof GraphExplorer.setSizeScale === 'function' && GraphExplorer.state) {
            var scale = GraphExplorer.state.currentSizeScale;
            if (scale && scale !== 1) {
                GraphExplorer.setSizeScale(scale);
            }
        }

        // 4. Apply correct font color for current theme
        if (typeof GraphExplorer.updateNodeFontColors === 'function') {
            var theme = (typeof GraphExplorer.getTheme === 'function')
                ? GraphExplorer.getTheme() : 'dark';
            GraphExplorer.updateNodeFontColors(theme);
        }
    }

    /**
     * Reveal hidden edges connected to a set of node IDs.
     * Called when a cluster is expanded to un-hide edges that were
     * kept hidden during deferred edge rendering.
     */
    function _revealEdgesForNodes(nodeIds) {
        var edges = _getEdges();
        if (!edges || !nodeIds || nodeIds.length === 0) return;

        var nodeSet = {};
        for (var i = 0; i < nodeIds.length; i++) nodeSet[nodeIds[i]] = true;

        var allEdges = edges.get();
        var updates = [];
        for (var j = 0; j < allEdges.length; j++) {
            var e = allEdges[j];
            if (e.hidden && (nodeSet[e.from] || nodeSet[e.to])) {
                updates.push({ id: e.id, hidden: false });
            }
        }

        if (updates.length > 0) {
            edges.update(updates);
            console.log('[ExplorerClustering] Revealed ' + updates.length + ' hidden edges for expanded cluster');
        }
    }

    /**
     * Reveal ALL hidden edges — used when all clusters are expanded.
     */
    function _revealAllEdges() {
        var edges = _getEdges();
        if (!edges) return;
        var allEdges = edges.get();
        var updates = [];
        for (var i = 0; i < allEdges.length; i++) {
            if (allEdges[i].hidden) {
                updates.push({ id: allEdges[i].id, hidden: false });
            }
        }
        if (updates.length > 0) {
            edges.update(updates);
            console.log('[ExplorerClustering] Revealed all ' + updates.length + ' hidden edges');
        }
    }

    /**
     * Return the correct cluster label font color for the current theme.
     */
    function _getClusterFontColor() {
        var theme = 'dark';
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.getTheme) {
            theme = GraphExplorer.getTheme();
        } else if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
            theme = GraphExplorer.state.theme || 'dark';
        }
        return theme === 'light' ? '#212529' : '#ffffff';
    }

    /**
     * Update font colors for all active cluster nodes when theme changes.
     */
    function updateClusterTheme() {
        var network = _getNetwork();
        if (!network) return;
        var fontColor = _getClusterFontColor();
        var clusterIds = Object.keys(clusterState.activeClusters);
        clusterIds.forEach(function(clusterId) {
            try {
                var body = network.body;
                if (body && body.nodes && body.nodes[clusterId]) {
                    var node = body.nodes[clusterId];
                    // Update the options object
                    if (node.options && node.options.font) {
                        node.options.font.color = fontColor;
                    }
                    // vis.js caches font rendering in the labelModule —
                    // we must update that too or the old color persists.
                    if (node.labelModule) {
                        if (node.labelModule.fontOptions) {
                            node.labelModule.fontOptions.color = fontColor;
                        }
                        // Clear the label's measurement cache so it redraws
                        // with the new color.
                        if (node.labelModule.labelDirty !== undefined) {
                            node.labelModule.labelDirty = true;
                        }
                    }
                }
            } catch(e) { /* cluster may not exist */ }
        });
        if (clusterIds.length > 0 && network.redraw) {
            network.redraw();
        }
    }

    /**
     * Darken a hex color by a factor (0-1).
     */
    function _darkenColor(hex, factor) {
        try {
            var r = parseInt(hex.slice(1, 3), 16);
            var g = parseInt(hex.slice(3, 5), 16);
            var b = parseInt(hex.slice(5, 7), 16);
            r = Math.round(r * (1 - factor));
            g = Math.round(g * (1 - factor));
            b = Math.round(b * (1 - factor));
            return '#' + [r, g, b].map(function(c) {
                return c.toString(16).padStart(2, '0');
            }).join('');
        } catch(e) {
            return hex;
        }
    }

    /**
     * Lighten a hex color by a factor (0-1).
     */
    function _lightenColor(hex, factor) {
        try {
            var r = parseInt(hex.slice(1, 3), 16);
            var g = parseInt(hex.slice(3, 5), 16);
            var b = parseInt(hex.slice(5, 7), 16);
            r = Math.min(255, Math.round(r + (255 - r) * factor));
            g = Math.min(255, Math.round(g + (255 - g) * factor));
            b = Math.min(255, Math.round(b + (255 - b) * factor));
            return '#' + [r, g, b].map(function(c) {
                return c.toString(16).padStart(2, '0');
            }).join('');
        } catch(e) {
            return hex;
        }
    }

    // ============================================
    // Public API
    // ============================================
    global.ExplorerClustering = {
        init: init,
        // Core clustering operations
        clusterByAttribute: clusterByAttribute,
        clusterByHubsize: clusterByHubsize,
        expandCluster: expandCluster,
        expandAll: expandAll,
        collapseAll: collapseAll,
        // Auto-clustering
        autoClusterIfNeeded: autoClusterIfNeeded,
        // Theme
        updateClusterTheme: updateClusterTheme,
        // Queries
        isCluster: isCluster,
        getClusterMembers: getClusterMembers,
        getClusterInfo: getClusterInfo,
        getClusterStats: getClusterStats,
        // Configuration
        setAutoCluster: setAutoCluster,
        setClusterThreshold: setClusterThreshold,
        setClusterAttribute: setClusterAttribute,
        setMinClusterSize: setMinClusterSize,
        setMaxClusters: setMaxClusters,
        reclusterCurrentGraph: reclusterCurrentGraph,
        // State getters
        get enabled() { return clusterState.enabled; },
        get autoClusterEnabled() { return clusterState.autoCluster; },
        get clusterThreshold() { return clusterState.clusterThreshold; },
        get clusterAttribute() { return clusterState.clusterAttribute; },
        get maxClusters() { return clusterState.maxClusters; },
    };

})(typeof window !== 'undefined' ? window : this);
